import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiService, BasketAssociation, BasketFamily, BasketLevel, BasketSource } from '../services/api';
import StockBadge, { stockSummary } from './StockBadge';

const fmtPct = (v: number | null): string => {
  if (v === null || v === undefined) return '-';
  return (v * 100).toFixed(2) + '%';
};

const fmtNum = (v: number | null, digits = 2): string => {
  if (v === null || v === undefined) return '-';
  return v.toFixed(digits);
};

const liftBadge = (lift: number | null): JSX.Element => {
  if (lift === null || lift === undefined) return <span className="text-muted">-</span>;
  let cls = 'badge bg-secondary';
  if (lift >= 5) cls = 'badge bg-success';
  else if (lift >= 2.5) cls = 'badge bg-primary';
  else if (lift >= 1.5) cls = 'badge bg-info text-dark';
  return <span className={cls}>{lift.toFixed(2)}x</span>;
};

const SOURCE_BADGE: Record<BasketSource, { label: string; cls: string; title: string }> = {
  sku: { label: 'SKU', cls: 'bg-dark', title: 'Comprado com este SKU' },
  family_fine: {
    label: 'Família (modelo)', cls: 'bg-primary',
    title: 'Comprado com SKUs do mesmo modelo/socket (categoria + marca + filtro)',
  },
  family_coarse: {
    label: 'Família', cls: 'bg-info text-dark',
    title: 'Comprado com SKUs da mesma categoria e marca',
  },
};

const sourceBadge = (r: BasketAssociation): JSX.Element => {
  const b = SOURCE_BADGE[r.source ?? 'sku'];
  const extra = r.source && r.source !== 'sku'
    ? ` · ${r.n_source_skus ?? 0} SKU(s) da família compraram este produto`
    : '';
  return <span className={`badge ${b.cls}`} title={b.title + extra}>{b.label}</span>;
};

const familyLabel = (f: BasketFamily): string =>
  [f.category, f.brand, f.key_value].filter(Boolean).join(' · ');

const BasketProductPanel: React.FC = () => {
  const [sku, setSku] = useState('');
  const [submittedSku, setSubmittedSku] = useState('');
  const [submittedName, setSubmittedName] = useState<string | null>(null);
  const [submittedOnline, setSubmittedOnline] = useState<boolean | null>(null);
  const [submittedInStock, setSubmittedInStock] = useState<boolean | null>(null);
  const [results, setResults] = useState<BasketAssociation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Default confidence, nao lift: lift favorece pares raros (um produto que so
  // aparece em 3 cestos, todos com este SKU, da lift ~80 com co_count=3).
  const [orderBy, setOrderBy] = useState<'lift' | 'confidence' | 'co_count' | 'support'>('confidence');
  const [limit, setLimit] = useState(20);
  const [minRelatedBaskets, setMinRelatedBaskets] = useState(10);
  const [inStockOnly, setInStockOnly] = useState(true);
  // Auto: SKU primeiro, a família completa (dá relacionados a produtos sem vendas)
  const [level, setLevel] = useState<BasketLevel>('auto');
  const [family, setFamily] = useState<BasketFamily | null>(null);
  const requestRef = useRef(0);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = sku.trim();
    if (!trimmed) return;
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    setSubmittedSku(trimmed);
    try {
      const resp = await apiService.getBasketAssociationsForSku(trimmed, {
        limit,
        order_by: orderBy,
        min_related_baskets: minRelatedBaskets,
        in_stock_only: inStockOnly,
        level,
      });
      if (reqId !== requestRef.current) return; // resposta obsoleta — ignorar
      setResults(resp.results);
      setSubmittedName(resp.sku_name ?? null);
      setSubmittedOnline(resp.sku_online ?? null);
      setSubmittedInStock(resp.sku_in_stock ?? null);
      setFamily(resp.family ?? null);
    } catch (err: any) {
      if (reqId !== requestRef.current) return;
      setError(err?.response?.data?.error || err?.message || 'Erro a carregar associações');
      setResults([]);
      setSubmittedName(null);
      setSubmittedOnline(null);
      setSubmittedInStock(null);
      setFamily(null);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSearch} className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label small mb-1">SKU</label>
          <input
            type="text"
            className="form-control"
            placeholder="Ex: P012345"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label
            className="form-label small mb-1"
            title="SKU: pares comprados com este SKU. Família: comprados com SKUs da mesma categoria + marca (e modelo/socket). Auto: SKU primeiro, a família completa."
          >
            Nível
          </label>
          <select
            className="form-select"
            value={level}
            onChange={(e) => setLevel(e.target.value as BasketLevel)}
          >
            <option value="auto">Auto (SKU + família)</option>
            <option value="sku">SKU → SKU</option>
            <option value="family">Família → SKU</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label small mb-1">Ordenar por</label>
          <select
            className="form-select"
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value as any)}
          >
            <option value="confidence">Confidence (SKU → outro)</option>
            <option value="co_count">Co-Count</option>
            <option value="lift">Lift (favorece pares raros)</option>
            <option value="support">Support</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label small mb-1">Top N</label>
          <input
            type="number"
            className="form-control"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => { const n = parseInt(e.target.value, 10); setLimit(Number.isFinite(n) ? n : 20); }}
          />
        </div>
        <div className="col-md-2">
          <label
            className="form-label small mb-1"
            title="Suporte mínimo do produto relacionado, em nº de cestos. Corta pares sem significância estatística (0 desliga)."
          >
            Cestos mín. do par
          </label>
          <input
            type="number"
            className="form-control"
            min={0}
            value={minRelatedBaskets}
            onChange={(e) => { const n = parseInt(e.target.value, 10); setMinRelatedBaskets(Number.isFinite(n) && n >= 0 ? n : 0); }}
          />
        </div>
        <div className="col-md-2 d-flex align-items-center">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="fbtInStockOnly"
              checked={inStockOnly}
              onChange={(e) => setInStockOnly(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="fbtInStockOnly" title="Stock do site (Saleor), actualizado antes de cada ranking">
              Só com stock
            </label>
          </div>
        </div>
        <div className="col-md-2">
          <button type="submit" className="btn btn-primary w-100" disabled={loading || !sku.trim()}>
            {loading ? 'A procurar...' : 'Procurar'}
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      {submittedSku && !loading && !error && (
        <div className="mb-2">
          <h5 className="mb-1">
            <span className="fw-bold">{submittedSku}</span>
            {submittedName ? <span className="text-muted fw-normal"> · {submittedName}</span> : null}
            {submittedOnline === false ? (
              <span className="badge bg-warning text-dark ms-2" title="Produto não está online">offline</span>
            ) : null}
            <StockBadge inStock={submittedInStock} className="ms-2" />
          </h5>
          {family && (
            <p className="small mb-1">
              <span className="text-muted">Família:</span> {familyLabel(family)}
            </p>
          )}
          <p className="text-muted small mb-0">
            {results.length === 0
              ? 'Sem produtos associados.'
              : `${results.length} produto(s) comprado(s) em conjunto (${stockSummary(results.map((r) => r.related_in_stock))}).`}
          </p>
        </div>
      )}

      {results.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Produto relacionado</th>
                <th>Stock</th>
                <th>Origem</th>
                <th className="text-end">Co-Count</th>
                <th className="text-end" title="Nº de cestos em que o produto relacionado aparece, sozinho ou não. Valores baixos tornam o lift pouco fiável.">
                  Cestos do par
                </th>
                <th className="text-end" title="Nas linhas de família: P(relacionado | cesto com algum SKU da família)">
                  P(relacionado | {submittedSku})
                </th>
                <th className="text-end" title="Lift = P(A∩B)/(P(A)·P(B)). Favorece pares raros: com poucos cestos o valor dispara sem significado prático.">
                  Lift
                </th>
                <th className="text-end">Support</th>
                <th className="text-end">Jaccard</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, idx) => (
                <tr key={`${r.source ?? 'sku'}_${r.related_sku}`}>
                  <td className="text-muted">{idx + 1}</td>
                  <td>
                    <Link to={`/products/${r.related_sku}`} className="text-decoration-none">
                      <span className="fw-semibold">{r.related_sku}</span>
                      {r.related_name ? (
                        <span className="text-muted ms-1">· {r.related_name}</span>
                      ) : null}
                    </Link>
                    {r.related_online === false ? (
                      <span className="badge bg-warning text-dark ms-1" title="Produto não está online">offline</span>
                    ) : null}
                  </td>
                  <td><StockBadge inStock={r.related_in_stock} /></td>
                  <td>{sourceBadge(r)}</td>
                  <td className="text-end">{r.co_count.toLocaleString('pt-PT')}</td>
                  <td className={`text-end ${(r.related_baskets ?? 0) < 10 ? 'text-warning' : 'text-muted'}`}>
                    {r.related_baskets !== undefined ? r.related_baskets.toLocaleString('pt-PT') : '-'}
                  </td>
                  <td className="text-end">{fmtPct(r.confidence)}</td>
                  <td className="text-end">{liftBadge(r.lift)}</td>
                  <td className="text-end">{fmtPct(r.support)}</td>
                  <td className="text-end">{fmtNum(r.jaccard, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BasketProductPanel;
